import * as corgi from 'external/dev_april_corgi+/js/corgi';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {EmptyDeps} from 'external/dev_april_corgi+/js/corgi/deps';
import {CorgiEvent, DOM_MOUSE} from 'external/dev_april_corgi+/js/corgi/events';
import {ACTION, CLOSE} from 'external/dev_april_corgi+/js/emu/events';

import {Button} from './widgets';

interface GraphInfo {
  id: string;
  name: string;
  lastModified: number;
}

interface Args {
  graphs: GraphInfo[];
  currentGraphId?: string;
  onSelect: (id: string) => void;
}

interface State {
  selectedId: string | undefined;
}

export class OpenGraphDialogController extends Controller<
  Args,
  EmptyDeps,
  HTMLElement,
  State
> {
  private readonly onSelect: (id: string) => void;

  constructor(response: Response<OpenGraphDialogController>) {
    super(response);
    this.onSelect = response.args.onSelect;
  }

  selectGraph(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const id = e.actionElement.data('id')?.string();
    if (!id) return;

    this.updateState({
      ...this.state,
      selectedId: id,
    });
  }

  openSelected(): void {
    if (this.state.selectedId) {
      this.onSelect(this.state.selectedId);
      this.trigger(CLOSE, {kind: 'resolve'});
    }
  }
}

export function makeOpenGraphDialog(args: Args) {
  return <OpenGraphDialog {...args} />;
}

function OpenGraphDialog(
  args: Args,
  state: State | undefined,
  updateState: (newState: State) => void,
) {
  if (!state) {
    state = {
      selectedId: args.currentGraphId ?? args.graphs[0]?.id,
    };
  }

  return (
    <div
      className="bg-neutral-800 flex flex-col gap-4 min-w-[400px] p-4 rounded"
      js={corgi.bind({
        controller: OpenGraphDialogController,
        args,
        state: [state, updateState],
      })}
    >
      <div className="font-semibold text-lg">Open Graph</div>

      <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
        {args.graphs.map(graph => (
          <GraphRow graph={graph} isSelected={state!.selectedId === graph.id} />
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <Button unboundEvents={{corgi: [[ACTION, 'openSelected']]}}>
          Open
        </Button>
      </div>
    </div>
  );
}

function GraphRow({
  graph,
  isSelected,
}: {
  graph: GraphInfo;
  isSelected: boolean;
}) {
  const date = new Date(graph.lastModified);
  const dateStr = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();

  return (
    <div
      className={
        'cursor-pointer flex justify-between p-2 rounded ' +
        (isSelected ? 'bg-neutral-600' : 'hover:bg-neutral-700')
      }
      data={{id: graph.id}}
      unboundEvents={{
        click: 'selectGraph',
      }}
    >
      <div>{graph.name}</div>
      <div className="text-neutral-400 text-sm">{dateStr}</div>
    </div>
  );
}
